import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, Plus, Pencil } from "lucide-react";

export default function Locations() {
  const { canWrite } = useAuth();
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", vat_number: "", active: true, category_scheme: "singh_4", match_keyword: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/locations").then((r) => setLocations(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", address: "", vat_number: "", active: true, category_scheme: "singh_4", match_keyword: "" }); setOpen(true); };
  const openEdit = (l) => { setEditing(l.id); setForm({ name: l.name, address: l.address, vat_number: l.vat_number, active: l.active, category_scheme: l.category_scheme || "singh_4", match_keyword: l.match_keyword || "" }); setOpen(true); };

  const submit = async () => {
    if (!form.name) return toast.error("Naam verplicht");
    setSaving(true);
    try {
      if (editing) await api.put(`/locations/${editing}`, form);
      else await api.post("/locations", form);
      toast.success("Vestiging opgeslagen");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Store className="h-7 w-7 text-primary" /> Vestigingen
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Bedrijfsvestigingen voor gecentraliseerde ontvangstenregistratie</p>
        </div>
        {canWrite && <Button onClick={openNew} data-testid="open-location"><Plus className="h-4 w-4 mr-2" /> Nieuwe vestiging</Button>}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Naam</TableHead><TableHead>Adres</TableHead><TableHead>BTW-nummer</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {locations.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nog geen vestigingen. Voeg er een toe om te beginnen.</TableCell></TableRow>}
              {locations.map((l) => (
                <TableRow key={l.id} data-testid={`location-row-${l.id}`}>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.address || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{l.vat_number || "—"}</TableCell>
                  <TableCell>{l.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Actief</Badge> : <Badge variant="outline">Inactief</Badge>}</TableCell>
                  <TableCell>{canWrite && <Button variant="ghost" size="sm" onClick={() => openEdit(l)} data-testid={`edit-location-${l.id}`}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Vestiging bewerken" : "Nieuwe vestiging"}</DialogTitle><DialogDescription>Beheer de gegevens van deze bedrijfsvestiging.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Naam</Label><Input data-testid="location-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Adres</Label><Input data-testid="location-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>BTW-nummer</Label><Input data-testid="location-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="BE0788952072" /></div>
            <div className="space-y-1.5">
              <Label>Categorie-indeling (Z-ticket)</Label>
              <Select value={form.category_scheme} onValueChange={(v) => setForm({ ...form, category_scheme: v })}>
                <SelectTrigger data-testid="location-scheme"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="singh_4">Singh Express — 4 categorieën (Kranten bij Tabak)</SelectItem>
                  <SelectItem value="leesberg_5">De Leesberg — 5 categorieën (aparte Kranten)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Bestandsnaam-trefwoord (auto-koppeling Z-ticket)</Label><Input data-testid="location-keyword" value={form.match_keyword} onChange={(e) => setForm({ ...form, match_keyword: e.target.value.toLowerCase() })} placeholder="brustem / sint-truiden / leesberg" /></div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={saving} data-testid="submit-location">{saving ? "Bezig…" : "Opslaan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
